import { Dialog, Transition, Switch, RadioGroup } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { ExclamationIcon, StarIcon } from "@heroicons/react/solid";
import {
  Fragment,
  ReactNode,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import DangerButton from "../buttons/danger";
import ErrorCallout from "../callouts/error";
import TextInput from "../input/text";
import NeutralModal from "./neutral";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/auth";
import { useApi } from "../../hooks/api";
import {
  PrepCenter,
  PrepCenterLocation,
  SupportedModel,
  Turnaround,
} from "../../model/prepCenter";
import classNames from "../../util/classNames";
import countries from "i18n-iso-countries";
import { Review } from "../../model/review";

export default function UpdateReviewModal({
  open,
  setOpen,
  review,
  onCreate,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  review: Review | null;
  onCreate?: () => void;
}) {
  const formRef: React.RefObject<HTMLFormElement> = useRef(null);
  const { authUser, dbUser, prepCenter, updateUser } = useAuth();
  const { updateReview, deleteReview } = useApi();
  const [loading, setLoading] = useState(true);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [fieldValidity, setFieldValidity] = useState<{
    [key: string]: boolean;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sourceReview, setSourceReview] = useState<{
    [key: string]: any;
  }>({});
  const [stagedReview, setStagedReview] = useState<{
    [key: string]: any;
  }>({});

  const handleClear = useCallback(async () => {
    if (!review) return;

    const snapshot = {
      rating: review.rating,
      review: review.review,
    };

    // Reset fields
    setSourceReview({ ...snapshot });
    setStagedReview({ ...snapshot });

    // Reset required field validation
    setReviewError(null);
    setFieldsRequired(false);
    setFieldValidity({});
  }, [
    review,
    setSourceReview,
    setStagedReview,
    setFieldsRequired,
    setFieldValidity,
  ]);

  const unsavedChanges = useCallback(() => {
    for (const prop in stagedReview) {
      if (Object.hasOwn(stagedReview, prop)) {
        if (Array.isArray(stagedReview[prop])) {
          // Assumption: There are no duplicates in either array
          // Assumption: Items within arrays can be compared accurately with ===
          if (
            stagedReview[prop].length !== sourceReview[prop].length ||
            !stagedReview[prop].every((v: any) =>
              sourceReview[prop].includes(v)
            )
          )
            return true;
        } else if (stagedReview[prop] !== sourceReview[prop]) return true;
      }
    }
    return false;
  }, [sourceReview, stagedReview]);

  const handleSave = useCallback(async () => {
    if (!review) return;

    if (!stagedReview.rating) {
      setFieldsRequired(true);
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
      throw new Error("Rating is a required field.");
    }
    if (Object.values(fieldValidity).includes(false)) {
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
      throw new Error(
        `Invalid fields: ${Object.entries(fieldValidity)
          .filter(([_, v]) => !v)
          .map(([k, _]) => k)
          .join(", ")}.`
      );
    }

    setLoading(true);
    // Find the diff between current state and staged changes
    const diffReview: { [key: string]: any } = {};
    for (const prop in stagedReview) {
      if (Object.hasOwn(stagedReview, prop)) {
        if (Array.isArray(stagedReview[prop])) {
          // Assumption: There are no duplicates in either array
          // Assumption: Items within arrays can be compared accurately with ===
          if (
            stagedReview[prop].length !== sourceReview[prop].length ||
            !stagedReview[prop].every((v: any) =>
              sourceReview[prop].includes(v)
            )
          )
            diffReview[prop] = stagedReview[prop];
        } else if (stagedReview[prop] !== sourceReview[prop])
          diffReview[prop] = stagedReview[prop];
      }
    }

    let { status, errorMessage } = await updateReview(
      review?.prepCenterId,
      diffReview
    );
    if (errorMessage) {
      toast(`Error: ${errorMessage}`);
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
      throw new Error(errorMessage);
    } else if (status === 200) {
      toast("Updated review.");
      if (onCreate) onCreate();
    }
    setLoading(false);
  }, [
    review,
    stagedReview,
    fieldValidity,
    updateReview,
    sourceReview,
    onCreate,
  ]);

  const handleDelete = useCallback(async () => {
    if (!review) return;

    const { status } = await deleteReview(review?.prepCenterId as string, {
      toastError: true,
      toastSuccess: true,
    });
    if (onCreate) onCreate();
    setOpen(false);
  }, [deleteReview, review, setOpen, onCreate]);

  useEffect(() => {
    handleClear();
  }, [handleClear]);

  return (
    authUser && (
      <NeutralModal
        open={open}
        setOpen={setOpen}
        title="Edit your review"
        description="This review will appear on the prep center's page for others to read."
        saveText="Save review"
        saveDisabled={!unsavedChanges()}
        handle={handleSave}
        clear={handleClear}
      >
        {/*<div className="flex gap-16">
          <code className="text-gray-700 whitespace-pre">
            {`
            ${JSON.stringify(sourceReview, null, 2)}
          `.trim()}
          </code>
          <code className="text-gray-700 whitespace-pre">
            {`
            ${JSON.stringify(stagedReview, null, 2)}
          `.trim()}
          </code>
        </div>
        <code className="text-gray-700 whitespace-pre">
          unsavedChanges: {JSON.stringify(unsavedChanges())}
        </code>*/}

        <form className="space-y-8 divide-y divide-gray-200" ref={formRef}>
          <div className="space-y-8 divide-y divide-gray-200">
            <div>
              <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <RadioGroup
                  value={stagedReview.rating}
                  onChange={(e) =>
                    setStagedReview({
                      ...stagedReview,
                      rating: e,
                    })
                  }
                  className="m:col-span-6"
                >
                  <RadioGroup.Label className="block text-sm font-medium text-gray-700">
                    Rating
                  </RadioGroup.Label>

                  <div className="mt-2 flex items-center space-x-2">
                    {[0, 1, 2, 3, 4].map((r) => (
                      <RadioGroup.Option
                        key={r}
                        value={r + 1}
                        className="cursor-pointer"
                      >
                        <RadioGroup.Label as="span" className="sr-only">
                          {r} out of 5 stars
                        </RadioGroup.Label>
                        <StarIcon
                          className={classNames(
                            stagedReview.rating > r
                              ? "text-yellow-400"
                              : "text-gray-300",
                            "h-8 w-8 flex-shrink-0"
                          )}
                          aria-hidden="true"
                        />
                      </RadioGroup.Option>
                    ))}
                  </div>

                  <p className="sr-only">
                    {stagedReview.rating} out of 5 stars
                  </p>
                </RadioGroup>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="review"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Review
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="review"
                      name="review"
                      rows={6}
                      value={stagedReview.review || ""}
                      onChange={(e) =>
                        setStagedReview({
                          ...stagedReview,
                          review: e.target.value,
                        })
                      }
                      placeholder="(Optional) Leave a detailed review to help other sellers."
                      className="shadow-sm focus:ring-vendrive-500 focus:border-vendrive-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <div className="sm:col-span-6">
                <div className="flex justify-between flex-col sm:flex-row gap-6">
                  <span className="flex-grow flex flex-col">
                    <span className="text-sm font-medium text-gray-900">
                      Delete your review
                    </span>
                    <span className="text-sm text-gray-500">
                      Removes your review of the prep center. Not undoable.
                    </span>
                  </span>

                  <div className="my-auto">
                    <DangerButton onClick={handleDelete}>
                      Delete your review
                    </DangerButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </NeutralModal>
    )
  );
}
